/**
 * CortexModulationProvenance — Plano-14 Onda 1
 * Mostra proveniência REAL das modulações ToM/Affect: cada linha é uma
 * decisão que mudou a resposta de verdade (não só telemetria).
 * Sem placebo: se a tabela está vazia, é porque o Cortex não modulou nada
 * no período — não inventamos linha.
 */
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, RefreshCw, Brain, Activity, ShieldCheck } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

type Window = "1h" | "24h" | "7d";

interface ModulationRow {
  id: string;
  org_id: string;
  group_id: string | null;
  node: "affect_governance" | "tom_governance";
  proposed_action: string | null;
  final_action: string | null;
  proposed_decision: string | null;
  final_decision: string | null;
  reason: string;
  confidence: number | null;
  inferred_state: Record<string, unknown>;
  proposed_text_preview: string | null;
  final_text_preview: string | null;
  created_at: string;
}

const WINDOW_HOURS: Record<Window, number> = { "1h": 1, "24h": 24, "7d": 24 * 7 };

function nodeBadge(node: ModulationRow["node"]) {
  return node === "affect_governance" ? (
    <Badge variant="secondary" className="gap-1"><Activity className="h-3 w-3" /> Affect</Badge>
  ) : (
    <Badge variant="secondary" className="gap-1"><Brain className="h-3 w-3" /> ToM</Badge>
  );
}

export default function CortexModulationProvenance() {
  const [rows, setRows] = useState<ModulationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [windowSel, setWindowSel] = useState<Window>("24h");
  const [nodeFilter, setNodeFilter] = useState<"all" | ModulationRow["node"]>("all");
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const since = new Date(Date.now() - WINDOW_HOURS[windowSel] * 3600_000).toISOString();
      let q = supabase
        .from("cortex_modulation_log")
        .select("*")
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(200);
      if (nodeFilter !== "all") q = q.eq("node", nodeFilter);
      const { data, error } = await q;
      if (error) throw error;
      setRows((data ?? []) as ModulationRow[]);
    } catch (e) {
      setError((e as Error).message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowSel, nodeFilter]);

  const counts = useMemo(() => {
    const affect = rows.filter((r) => r.node === "affect_governance").length;
    const tom = rows.filter((r) => r.node === "tom_governance").length;
    return { affect, tom, total: rows.length };
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" /> Proveniência da modulação Cortex
          </h2>
          <p className="text-sm text-muted-foreground">
            Cada linha é uma modulação real aplicada à resposta. Sem placebo: vazio = Cortex não governou no período.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={windowSel} onValueChange={(v) => setWindowSel(v as Window)}>
            <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">Última hora</SelectItem>
              <SelectItem value="24h">Últimas 24h</SelectItem>
              <SelectItem value="7d">Últimos 7d</SelectItem>
            </SelectContent>
          </Select>
          <Select value={nodeFilter} onValueChange={(v) => setNodeFilter(v as any)}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os nós</SelectItem>
              <SelectItem value="affect_governance">Affect</SelectItem>
              <SelectItem value="tom_governance">ToM</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">Total de modulações</div>
          <div className="text-2xl font-semibold">{counts.total}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">Affect</div>
          <div className="text-2xl font-semibold">{counts.affect}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">ToM</div>
          <div className="text-2xl font-semibold">{counts.tom}</div>
        </CardContent></Card>
      </div>

      {error && (
        <Card className="border-destructive"><CardContent className="pt-4 text-sm text-destructive">{error}</CardContent></Card>
      )}

      {!loading && rows.length === 0 && !error && (
        <Card><CardContent className="pt-6 text-sm text-muted-foreground text-center">
          Nenhuma modulação Cortex no período. Isso é proveniência real: o pipeline rodou sem precisar reescrever resposta.
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {rows.map((r) => {
          const before = r.node === "affect_governance" ? r.proposed_action : r.proposed_decision;
          const after = r.node === "affect_governance" ? r.final_action : r.final_decision;
          return (
            <Card key={r.id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
                  {nodeBadge(r.node)}
                  <Badge variant="outline" className="font-mono text-xs">{before ?? "?"} → {after ?? "?"}</Badge>
                  <code className="text-xs text-muted-foreground">{r.reason}</code>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(r.created_at), { addSuffix: true, locale: ptBR })}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 text-xs space-y-2">
                {r.confidence !== null && (
                  <div><span className="text-muted-foreground">Confiança:</span> {(r.confidence * 100).toFixed(0)}%</div>
                )}
                {Object.keys(r.inferred_state ?? {}).length > 0 && (
                  <div className="font-mono bg-muted/40 rounded p-2 overflow-x-auto">
                    {JSON.stringify(r.inferred_state, null, 0)}
                  </div>
                )}
                {(r.proposed_text_preview || r.final_text_preview) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <div className="text-muted-foreground mb-1">Antes</div>
                      <div className="bg-muted/30 rounded p-2 line-clamp-4">{r.proposed_text_preview ?? "—"}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground mb-1">Depois</div>
                      <div className="bg-muted/30 rounded p-2 line-clamp-4">{r.final_text_preview ?? "—"}</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
